import { FragmentRoutes } from '~api';
import { enhanceFragment } from './fragment-enhancer';

/**
 * @desc Why a fragment failed to load, as the UI needs to tell it apart: `signed-out` (401 — no
 * or bad token; show a sign-in state), `forbidden` (403 — signed in without platform access) and
 * `failed` (anything else). The loader never retries any of them.
 */
export type FragmentFailure = 'signed-out' | 'forbidden' | 'failed';

/** @desc The words for each failure, for a page that renders its own error state around them. */
export const FRAGMENT_FAILURE_COPY: Record<
  FragmentFailure,
  { title: string; body: string; color: string }
> = {
  'signed-out': {
    title: 'Sign in to see this section',
    body: 'Your session has ended. Sign in again to load it.',
    color: 'gray',
  },
  forbidden: {
    title: 'No access',
    body: 'Your account does not include this content.',
    color: 'yellow',
  },
  failed: {
    title: 'Could not load this section',
    body: 'Something went wrong loading it. Reload the page to try again.',
    color: 'red',
  },
};

/**
 * @desc A fragment response that was not 2xx. `status` lets a caller tell 403 — signed in, but
 * without platform access — from 401 (no or bad token) and every other failure.
 */
export class FragmentFetchError extends Error {
  readonly kind: FragmentFailure;

  constructor(
    readonly status: number,
    statusText: string,
  ) {
    super(
      status === 403
        ? 'You do not have access to insights (403 Forbidden)'
        : `Fragment fetch failed: ${status} ${statusText}`,
    );
    this.name = 'FragmentFetchError';
    this.kind = status === 401 ? 'signed-out' : status === 403 ? 'forbidden' : 'failed';
  }

  get forbidden(): boolean {
    return this.kind === 'forbidden';
  }

  get signedOut(): boolean {
    return this.kind === 'signed-out';
  }
}

/** @desc What to tell a reader about a failed fragment read: the copy for a fetch failure, else the message. */
export const fragmentErrorMessage = (error: Error): string =>
  error instanceof FragmentFetchError ? FRAGMENT_FAILURE_COPY[error.kind].body : error.message;

export type FragmentParams = Readonly<Record<string, string>>;
export type FragmentSearch = Readonly<Record<string, string | undefined>>;

/**
 * @desc The URL for a fragment path. `:name` segments are filled from `params` (encoded); `search`
 * entries that are `undefined` or empty are dropped; `scheme`, when given, becomes the
 * `FragmentRoutes.SCHEME_PARAM` search parameter every component route reads. Relative, so it goes
 * through the portal's `/fragments` rewrite (same origin) unless the path is absolute.
 */
export function fragmentUrl(
  path: string,
  {
    params = {},
    search = {},
    scheme,
  }: {
    params?: FragmentParams;
    search?: FragmentSearch;
    scheme?: FragmentRoutes.ColourScheme;
  } = {},
): string {
  const filled = path.replace(/:([A-Za-z][A-Za-z0-9_]*)/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Fragment path ${path} needs the "${name}" parameter`);
    return encodeURIComponent(value);
  });
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) if (value) query.set(key, value);
  if (scheme) query.set(FragmentRoutes.SCHEME_PARAM, scheme);
  const qs = query.toString();
  return qs ? `${filled}?${qs}` : filled;
}

/** @desc The bearer header for the current session; none without one (the gate then 401s). */
async function bearerHeaders(getAccessToken: () => Promise<string>): Promise<HeadersInit> {
  try {
    return { Authorization: `Bearer ${await getAccessToken()}` };
  } catch {
    // No authenticated session — proceed without a token; the resource server 401s.
    return {};
  }
}

/**
 * @desc Fetches a data fragment (`/fragments/d/*`) as JSON, with the user's token, through the same
 * `/fragments` rewrite as the component fragments (same origin, so no preflight; the data routes
 * also answer CORS for a direct caller). Resolves `null` on a 404 (an unknown subject — the route
 * says so rather than inventing an empty body); any other non-2xx rejects with a
 * {@link FragmentFetchError}, never retried. The caller validates the body against the route's
 * `Fragments` schema.
 */
export async function fetchFragmentData(
  path: string,
  {
    params,
    search,
    getAccessToken,
    signal,
  }: {
    params?: FragmentParams;
    search?: FragmentSearch;
    getAccessToken: () => Promise<string>;
    signal?: AbortSignal;
  },
): Promise<unknown> {
  const headers = await bearerHeaders(getAccessToken);
  signal?.throwIfAborted();
  const response = await fetch(fragmentUrl(path, { params, search }), {
    signal,
    headers: { ...headers, Accept: 'application/json' },
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new FragmentFetchError(response.status, response.statusText);
  return response.json();
}

type LoadFragmentOptions = {
  /** URL to fetch the HTML fragment from; wins over `path`. */
  url?: string;
  /** Fragment path, `:name` segments filled from `params` (see {@link fragmentUrl}). */
  path?: string;
  params?: FragmentParams;
  search?: FragmentSearch;
  /**
   * The portal's colour scheme: sent as the `scheme` search parameter and mirrored on the
   * container as `data-color-scheme`. The theme's CSS variables still come from the portal root.
   */
  scheme?: FragmentRoutes.ColourScheme;
  /** DOM element to inject the fragment into */
  container: HTMLElement;
  /** AbortSignal for cleanup on unmount — aborting also removes every listener the load added */
  signal?: AbortSignal;
  /** Callback for SPA link navigation (handles [data-spa-link] clicks) */
  onNavigate?: (path: string) => void;
  /** Resolves the current WorkOS access token; attached as `Authorization: Bearer` */
  getAccessToken: () => Promise<string>;
  /** Wire `data-enhance` behaviours after injection (default true). */
  enhance?: boolean;
};

export type LoadedFragment = {
  cleanup: () => void;
  /**
   * What the fragment says it rendered (`FragmentRoutes.FrameSummary`, read from its root's
   * attributes); `undefined` when it states nothing — the operator pages, or an older insights.
   */
  summary: FragmentRoutes.FrameSummary | undefined;
};

/** @desc The summary the injected fragment's root states, if any. */
function injectedSummary(container: HTMLElement): FragmentRoutes.FrameSummary | undefined {
  const root = container.querySelector(`[${FragmentRoutes.FRAME_ATTRIBUTES.state}]`);
  return root ? FragmentRoutes.readFrameSummary((name) => root.getAttribute(name)) : undefined;
}

/**
 * @desc Fetches a self-contained HTML fragment and injects it into a container element.
 * Uses Range.createContextualFragment() so that inline <script> tags execute correctly
 * (unlike innerHTML which silently ignores scripts).
 *
 * Links with [data-spa-link] are intercepted and routed through the onNavigate callback
 * instead of triggering full page loads; `data-enhance` elements get their light behaviour from
 * `fragment-enhancer.ts` (D1 — no hydration).
 *
 * A load whose `signal` aborts before injection rejects instead of writing into the container, so
 * a stale request cannot overwrite the page that replaced it. Callers call `cleanup` on teardown;
 * aborting the signal removes the listeners too. A non-2xx answer rejects with a
 * {@link FragmentFetchError} and is never retried — a 401 does not loop.
 */
export async function loadFragment({
  url,
  path,
  params,
  search,
  scheme,
  container,
  signal,
  onNavigate,
  getAccessToken,
  enhance = true,
}: LoadFragmentOptions): Promise<LoadedFragment> {
  if (!url && !path) throw new Error('loadFragment needs a url or a path');
  const target = url ?? fragmentUrl(path ?? '', { params, search, scheme });

  const headers = await bearerHeaders(getAccessToken);
  signal?.throwIfAborted();

  const response = await fetch(target, { signal, headers });

  if (!response.ok) {
    throw new FragmentFetchError(response.status, response.statusText);
  }

  const html = await response.text();
  signal?.throwIfAborted();

  // Parse the full HTML document and extract fragment content
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fragmentRoot = doc.getElementById('fragment-root');
  if (!fragmentRoot) {
    throw new Error('Fragment root not found in response');
  }

  const range = document.createRange();
  range.selectNodeContents(container);
  range.deleteContents();

  const fragment = range.createContextualFragment(fragmentRoot.innerHTML);
  container.appendChild(fragment);
  if (scheme) container.setAttribute('data-color-scheme', scheme);

  // Every listener this load adds hangs off one controller, aborted by cleanup or the caller.
  const listeners = new AbortController();
  signal?.addEventListener('abort', () => listeners.abort(), { once: true });

  const handleClick = (e: Event) => {
    const anchor = (e.target as HTMLElement).closest('a[data-spa-link]');
    if (!anchor) return;
    e.preventDefault();
    const href = anchor.getAttribute('href');
    if (href && onNavigate) {
      onNavigate(href);
    }
  };

  container.addEventListener('click', handleClick, { signal: listeners.signal });
  if (enhance) enhanceFragment(container, listeners.signal);

  return {
    summary: injectedSummary(container),
    cleanup: () => {
      listeners.abort();
      container.removeAttribute('data-color-scheme');
      container.innerHTML = '';
    },
  };
}
